// Clustering helpers for the Google Maps report map.
// Ported from the cleancy mobile app's mapClustering.ts. On web we read the
// viewport bounds/zoom straight from the Google Map, so the RN region<->zoom
// math is unnecessary — only the bbox conversion and color logic carry over.

import { ReportData } from "../../types/global.d";
import { COLORS, REPORT_STATUS } from "../../utils/reportProps";

export type MapBBox = [number, number, number, number];

export interface ReportPointProperties {
  reportId: string;
  status: number;
}

// Per-cluster aggregate produced by supercluster's map/reduce so a cluster's
// color is derived without walking all of its leaves.
export interface ClusterAggregate {
  hasUnresolved: boolean; // a verified-but-uncleaned report (red)
  hasPendingCleanup: boolean; // report+cleaning awaiting verification (amber)
  hasCompleted: boolean; // cleaning verified (green)
  hasFullyCompleted: boolean; // both report + cleaning verified (FULLY_COMPLETED)
}

export const initialClusterAggregate = (status: number): ClusterAggregate => ({
  hasUnresolved: status === REPORT_STATUS.REPORT_VERIFIED,
  hasPendingCleanup:
    status === REPORT_STATUS.REPORT_WITH_CLEANING ||
    status === REPORT_STATUS.VERIFIED_REPORT_WITH_CLEANING,
  hasCompleted:
    status === REPORT_STATUS.FULLY_COMPLETED ||
    status === REPORT_STATUS.CLEANING_VERIFIED_ONLY,
  hasFullyCompleted: status === REPORT_STATUS.FULLY_COMPLETED,
});

export const combineClusterAggregate = (
  acc: ClusterAggregate,
  next: ClusterAggregate
): void => {
  acc.hasUnresolved = acc.hasUnresolved || next.hasUnresolved;
  acc.hasPendingCleanup = acc.hasPendingCleanup || next.hasPendingCleanup;
  acc.hasCompleted = acc.hasCompleted || next.hasCompleted;
  acc.hasFullyCompleted = acc.hasFullyCompleted || next.hasFullyCompleted;
};

/** Cluster color from its aggregate, same priority as getClusterStatusColor. */
export const getClusterColor = (aggregate?: ClusterAggregate): string => {
  if (!aggregate) {
    return COLORS.greyColor;
  }
  if (aggregate.hasUnresolved) {
    return COLORS.errorColor;
  }
  if (aggregate.hasPendingCleanup) {
    return COLORS.warnColor;
  }
  if (aggregate.hasCompleted) {
    return COLORS.mainDarkerColor;
  }
  return COLORS.greyColor;
};

/**
 * Border colors for a cluster marker. A cluster containing BOTH a red pin
 * (verified report still needing cleaning) and a green pin (fully completed)
 * gets a doubled border — outer red, inner green — so a mixed cluster isn't
 * hidden behind a single color. Every other cluster keeps its single status
 * color. Mirrors the mobile app's getClusterBorderColors.
 */
export const getClusterBorderColors = (
  aggregate?: ClusterAggregate
): { color: string; secondaryColor?: string } => {
  if (aggregate?.hasUnresolved && aggregate?.hasFullyCompleted) {
    return { color: COLORS.errorColor, secondaryColor: COLORS.mainDarkerColor };
  }
  return { color: getClusterColor(aggregate) };
};

/**
 * Convert a Google Maps bounds to a supercluster bbox [west, south, east, north].
 * supercluster handles the antimeridian case where west > east internally.
 */
export const boundsToBBox = (
  bounds: google.maps.LatLngBounds
): MapBBox => {
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  return [sw.lng(), sw.lat(), ne.lng(), ne.lat()];
};

/**
 * Pick a cluster's color from the statuses of the reports it contains.
 * Priority: any unresolved report (red) > pending cleanup (amber) >
 * completed (green) > grey. Mirrors the mobile app's getClusterStatusColor.
 */
export const getClusterStatusColor = (reports: ReportData[]): string => {
  const statuses = reports.map((report) => Number(report.status));

  if (statuses.some((status) => status === REPORT_STATUS.REPORT_VERIFIED)) {
    return COLORS.errorColor;
  }

  if (
    statuses.some(
      (status) =>
        status === REPORT_STATUS.VERIFIED_REPORT_WITH_CLEANING ||
        status === REPORT_STATUS.REPORT_WITH_CLEANING
    )
  ) {
    return COLORS.warnColor;
  }

  if (
    statuses.some(
      (status) =>
        status === REPORT_STATUS.FULLY_COMPLETED ||
        status === REPORT_STATUS.CLEANING_VERIFIED_ONLY
    )
  ) {
    return COLORS.mainDarkerColor;
  }

  return COLORS.greyColor;
};

/** Format a cluster count compactly (1.2k for thousands). */
export const formatClusterCount = (count: number): string => {
  if (count >= 1000) {
    return `${Math.floor(count / 100) / 10}k`;
  }
  return String(count);
};
